import { useState, useEffect, useRef } from "react";
import "./AdvertisingAndMediaSection2.css";
import phoneImage from "../../../assets/Beykoy_Phone_Mockup.png";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBook, faAngleRight } from "@fortawesome/free-solid-svg-icons";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

const AdvertisingAndMediaSection2 = () => {
  const { t } = useTranslation("global");
  const { lang } = useParams();

  const [activeSlide, setActiveSlide] = useState(0);
  const sliderRef = useRef(null);
  const statItemRefs = useRef([]);

  // Statistics data
  const statistics = [
    {
      percentage: t("advertisingAndMediaSection2.statistics.first.percentage"),
      description: t(
        "advertisingAndMediaSection2.statistics.first.description"
      ),
    },
    {
      percentage: t("advertisingAndMediaSection2.statistics.second.percentage"),
      description: t(
        "advertisingAndMediaSection2.statistics.second.description"
      ),
    },
    {
      percentage: t("advertisingAndMediaSection2.statistics.third.percentage"),
      description: t(
        "advertisingAndMediaSection2.statistics.third.description"
      ),
    },
    {
      percentage: t("advertisingAndMediaSection2.statistics.fourth.percentage"),
      description: t(
        "advertisingAndMediaSection2.statistics.fourth.description"
      ),
    },
  ];

  // Set up intersection observer to detect which stat item is in view
  useEffect(() => {
    // Only set up the observer on mobile devices
    if (window.innerWidth < 992) {
      const options = {
        root: null,
        rootMargin: "0px",
        threshold: 0.6, // Stat item is considered in view when 60% visible
      };

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = statItemRefs.current.findIndex(
              (ref) => ref === entry.target
            );
            if (index !== -1) {
              setActiveSlide(index);
            }
          }
        });
      }, options);

      const currentRefs = statItemRefs.current;
      // Observe all stat item elements
      currentRefs.forEach((item) => {
        if (item) observer.observe(item);
      });

      return () => {
        currentRefs.forEach((item) => {
          if (item) observer.unobserve(item);
        });
      };
    }
  }, []);

  // Scroll to a specific stat item when indicator is clicked
  const scrollToStatItem = (index) => {
    if (statItemRefs.current[index]) {
      statItemRefs.current[index].scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "center",
      });
      setActiveSlide(index);
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <section
      className="advertisement-process"
      aria-label="Advertising Process and Statistics"
    >
      <article className="tailored-section" aria-labelledby="worry-title">
        <div className="container-amsection2">
          <div className="mockup-container">
            <img
              src={phoneImage}
              alt={t("advertisingAndMediaSection2.phoneImageAlt")}
              className="mockup-image"
              loading="lazy"
              width="300"
              height="600"
              title="Mobile Advertising Solution"
            />
          </div>
          <div className="text-container-amsection2">
            <h2 id="worry-title">
              {t("advertisingAndMediaSection2.worryTitle")}
            </h2>
            <p>{t("advertisingAndMediaSection2.worryDescription1")}</p>
            <p>{t("advertisingAndMediaSection2.worryDescription2")}</p>
            <Link
              to={`/${lang}/Pricing`}
              onClick={scrollToTop}
              className="cta"
              aria-label="View advertising pricing plans"
            >
              {t("advertisingAndMediaSection2.startButton")}
              <FontAwesomeIcon
                icon={faAngleRight}
                style={{ background: "rgba(0, 0, 0, 0)", size: "2px" }}
                aria-hidden="true"
              />
            </Link>
          </div>
        </div>
      </article>

      {/* Statistics Section */}
      <article
        className="statistics-section"
        aria-labelledby="advertising-stats-header"
      >
        <h2 id="advertising-stats-header" className="statistics-header">
          <FontAwesomeIcon icon={faBook} aria-hidden="true" />{" "}
          {t("advertisingAndMediaSection2.statisticsTitle")}
        </h2>

        <div className="statistics-wrapper">
          <section
            className="statistics"
            ref={sliderRef}
            aria-label="Advertising Statistics"
          >
            {statistics.map((stat, index) => (
              <div
                key={index}
                className="stat-item"
                ref={(el) => (statItemRefs.current[index] = el)}
                id={`stat-${index}`}
              >
                <div className="stat-accent-line"></div>
                <h3>{stat.percentage}</h3>
                <p>{stat.description}</p>
              </div>
            ))}
          </section>

          {/* Slider indicators for mobile */}
          <div
            className="slider-indicators"
            role="tablist"
            aria-label="Statistics navigation"
          >
            {statistics.map((stat, index) => (
              <div
                key={index}
                className={`slider-dot ${
                  activeSlide === index ? "active" : ""
                }`}
                onClick={() => scrollToStatItem(index)}
                role="tab"
                aria-selected={activeSlide === index}
                aria-controls={`stat-${index}`}
                aria-label={`Navigate to statistic ${index + 1}`}
                tabIndex={0}
              />
            ))}
          </div>
        </div>
      </article>
    </section>
  );
};

export default AdvertisingAndMediaSection2;
